import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluationStore } from '../../store/evaluationStore';
import './AutoSaveIndicator.css';

interface AutoSaveIndicatorProps {
  className?: string;
}

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({ className }) => {
  const { t, i18n } = useTranslation();
  const { 
    hasUnsavedChanges, 
    lastSaved, 
    autoSaveEnabled, 
    isLoading,
    error 
  } = useEvaluationStore();

  const formatLastSaved = (date: Date | null | string) => {
    if (!date) return t('autoSave.unsaved');
    
    // Convert string to Date if needed (happens when restored from localStorage)
    let dateObj: Date;
    try {
      if (typeof date === 'string') {
        dateObj = new Date(date);
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        return t('autoSave.unsaved');
      }
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return t('autoSave.unsaved');
      }
    } catch (e) {
      return t('autoSave.unsaved');
    }
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 60) {
      return t('autoSave.savedSecondsAgo', { seconds: diffSeconds });
    } else if (diffMinutes < 60) {
      return t('autoSave.savedMinutesAgo', { minutes: diffMinutes });
    } else {
      return t('autoSave.savedAtTime', { 
        time: dateObj.toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : i18n.language, { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      });
    }
  };

  const getStatusIcon = () => {
    if (error) return '⚠️';
    if (isLoading) return '💾';
    if (hasUnsavedChanges) return '●';
    return '✓';
  };

  const getStatusText = () => {
    if (error) return t('common.error');
    if (isLoading) return t('autoSave.saving');
    if (hasUnsavedChanges) return t('autoSave.unsavedChanges');
    return t('autoSave.saved');
  };

  const getStatusClass = () => {
    if (error) return 'status-error';
    if (isLoading) return 'status-saving';
    if (hasUnsavedChanges) return 'status-unsaved';
    return 'status-saved';
  };

  // コンパクトモード：保存時刻のみを表示
  return (
    <div className={`auto-save-indicator compact ${getStatusClass()} ${className || ''}`}>
      <span className="status-icon" title={getStatusText()}>{getStatusIcon()}</span>
      <span className="last-saved-text">{formatLastSaved(lastSaved)}</span>
      {error && (
        <span className="error-icon" title={error}>⚠️</span>
      )}
    </div>
  );
};

export default AutoSaveIndicator;